if (!customElements.get('mattress-variant-select')) {
  customElements.define(
    'mattress-variant-select',
    class MattressVariantSelect extends HTMLElement {
      connectedCallback() {
        this.nativeSelect = document.getElementById(this.dataset.nativeSelect);
        this.toggle = this.querySelector('[data-mattress-select-toggle]');
        this.list = this.querySelector('[data-mattress-select-list]');
        this.label = this.querySelector('[data-mattress-selected-label]');
        this.dimensions = this.querySelector('[data-mattress-selected-dimensions]');
        this.options = [...this.querySelectorAll('[data-option-value]')];
        this.variantSelects = [...(this.closest('variant-selects')?.querySelectorAll('select') || [])];
        const variantData = this.querySelector('[data-mattress-variant-prices]');
        this.variants = variantData ? JSON.parse(variantData.textContent) : [];

        if (!this.nativeSelect || !this.toggle || !this.list) return;

        this.toggle.addEventListener('click', () => this.setOpen(this.toggle.getAttribute('aria-expanded') !== 'true'));
        this.options.forEach((option) => option.addEventListener('click', () => this.selectOption(option)));
        this.nativeSelect.addEventListener('change', () => this.syncFromNative());
        this.onAnyNativeChange = () => this.updatePrices();
        this.variantSelects.forEach((select) => select.addEventListener('change', this.onAnyNativeChange));
        this.addEventListener('keydown', (event) => this.onKeydown(event));
        this.onDocumentClick = (event) => {
          if (!this.contains(event.target)) this.setOpen(false);
        };
        document.addEventListener('click', this.onDocumentClick);
        this.classList.add('is-ready');
        this.syncFromNative();
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onDocumentClick);
        this.variantSelects?.forEach((select) => select.removeEventListener('change', this.onAnyNativeChange));
      }

      setOpen(open) {
        if (open) {
          this.list.hidden = false;
          const toggleRect = this.toggle.getBoundingClientRect();
          const availableBelow = window.innerHeight - toggleRect.bottom;
          const availableAbove = toggleRect.top;
          const availableWidth = window.innerWidth - toggleRect.left - 16;
          const requiredSpace = Math.min(this.list.scrollHeight, 400);
          const dropUp = availableBelow < Math.min(requiredSpace, 190)
            && availableAbove > availableBelow + 80;
          const availableSpace = dropUp ? availableAbove : availableBelow;
          const menuWidth = toggleRect.width >= 320
            ? toggleRect.width
            : Math.max(toggleRect.width, Math.min(420, availableWidth));
          this.classList.toggle('is-drop-up', dropUp);
          this.list.style.width = `${menuWidth}px`;
          this.list.style.maxHeight = dropUp
            ? `${Math.max(80, Math.min(400, availableSpace - 8))}px`
            : '400px';
        }

        this.toggle.setAttribute('aria-expanded', String(open));
        this.list.hidden = !open;
        this.classList.toggle('is-open', open);

        if (!open) {
          this.classList.remove('is-drop-up');
          this.list.style.width = '';
          this.list.style.maxHeight = '';
        }
      }

      selectOption(option) {
        if (option.disabled) return;
        this.nativeSelect.value = option.dataset.optionValue;
        this.nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));
        this.syncFromNative();
        this.setOpen(false);
        this.toggle.focus();
      }

      syncFromNative() {
        const selected = this.options.find((option) => option.dataset.optionValue === this.nativeSelect.value);
        if (!selected) return;
        this.label.textContent = selected.dataset.optionLabel;
        if (this.dimensions) {
          this.dimensions.textContent = selected.dataset.optionDimensions
            ? `(${selected.dataset.optionDimensions})`
            : '';
        }
        this.options.forEach((option) => {
          const isSelected = option === selected;
          option.classList.toggle('is-selected', isSelected);
          option.setAttribute('aria-selected', String(isSelected));
        });
        this.updatePrices();
      }

      updatePrices() {
        if (!this.variants.length || !this.variantSelects.length) return;

        const optionPosition = this.variantSelects.indexOf(this.nativeSelect);
        const selectedOptions = this.variantSelects.map((select) => select.value);

        this.options.forEach((option) => {
          const candidateOptions = [...selectedOptions];
          candidateOptions[optionPosition] = option.dataset.optionValue;
          const variant = this.variants.find((item) =>
            item.options.every((value, index) => value === candidateOptions[index])
          );
          if (!variant) return;

          const compare = option.querySelector('[data-mattress-option-compare]');
          const price = option.querySelector('[data-mattress-option-price]');
          if (compare) {
            compare.innerHTML = variant.compareAtPrice > variant.price
              ? `<s>${variant.compareAtPriceFormatted}</s>`
              : '';
          }
          if (price) price.textContent = variant.priceFormatted;
          option.disabled = !variant.available;
        });
      }

      onKeydown(event) {
        if (event.key === 'Escape') {
          this.setOpen(false);
          this.toggle.focus();
          return;
        }

        if (!['ArrowDown', 'ArrowUp'].includes(event.key)) return;
        event.preventDefault();
        if (this.list.hidden) this.setOpen(true);
        const enabled = this.options.filter((option) => !option.disabled);
        const current = enabled.indexOf(document.activeElement);
        const next = event.key === 'ArrowDown'
          ? enabled[(current + 1 + enabled.length) % enabled.length]
          : enabled[(current - 1 + enabled.length) % enabled.length];
        next?.focus();
      }
    }
  );
}
